/* eslint-disable @typescript-eslint/naming-convention */
import { get, post } from '@apis/api';
import {
  CommonResponse,
  PageCommonResponse,
  PageInfoResponse,
} from '@models/common';
import {
  SpcAggsResponse,
  SpcAggsResult,
  SpcFilterNameResponse,
  SpcNavigationItem,
  SpcNavigationResponse,
  SpcProductCountResponse,
  SpcProductResponse,
} from '@models/spc';
import { DisplayAreaInfo } from '@wconcept/wconcept-common/models/renewal/display';
import { Ga4ClickEvent } from '@wconcept/wconcept-common/models/renewal/brand';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import type { CommonResponse as ApiCommonResponse } from '@models/api';
import { MainDisplayContentResponse } from './main';
import type { LpProductFilterType, LpProductRequest } from './lpProduct';

const baseURL = process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_URL;
const options = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'DISPLAY-API-KEY': process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_KEY || '',
  },
};

export interface SpcBrandListResponse extends CommonResponse {
  data: SpcBrandListContent[];
}

export interface SpcBrandListContent {
  roundNo: number;
  roundTitle: string;
  brandList: BrandItem[];
}

export interface BrandItem {
  brandCd: string;
  brandLogo: string;
  brandNameKr: string;
  brandName: string;
  exclusiveYn: string;
  imageUrl: string;
  landingUrl: string;
  ga4ClickEvent: Ga4ClickEvent;
}
export interface SpcDisplayResponse extends PageCommonResponse {
  data: SpcDisplayContent;
}

export interface SpcDisplayContent extends PageInfoResponse {
  content: DisplayAreaInfo[];
}

export interface SpcDisplayRequestParam {
  spcType: string;
  pageNo: number;
  pageSize: number;
  recommendItems: string[];
}

export interface RequestSpcProductListParams {
  custNo: string;
  gender: string;
  sort?: string;
  pageNo?: number;
  pageSize?: number;
  shopCds?: string[];
  bcds?: string[];
  mcd?: string;
  ccds?: string[];
  pcds?: string[];
  lcds?: string[];
  source?: string;
  device?: string;
  searchType?: string;
  priceSections?: number[];
  minPrice?: number;
  maxPrice?: number;
  colors?: string[];
  benefits?: string[];
  discounts?: string[];
  status?: string[];
  saleTags?: string[];
}

const postSpcLpProduct = async <TResponse>(
  url: string,
  params: LpProductRequest
): Promise<TResponse | undefined> => {
  const { data } = await post<ApiCommonResponse<TResponse>, LpProductRequest>({
    url,
    baseUrl: baseURL,
    options,
    data: params,
  });

  return data;
};

/**
 * 기획전 LP 상품 API v1
 * 초기 데이터, 상품 목록, 상품 수, 필터를 조회한다.
 */
export const requestSpcLpProductInit = async <TResponse>(
  specialCd: string,
  params: LpProductRequest
): Promise<TResponse | undefined> =>
  postSpcLpProduct(
    `/display/api/special/v1/products/init/${specialCd}`,
    params
  );

export const requestSpcLpProduct = async <TResponse>(
  specialCd: string,
  params: LpProductRequest
): Promise<TResponse | undefined> =>
  postSpcLpProduct(`/display/api/special/v1/products/${specialCd}`, params);

export const requestSpcLpProductCount = async <TResponse>(
  specialCd: string,
  params: LpProductRequest
): Promise<TResponse | undefined> =>
  postSpcLpProduct(
    `/display/api/special/v1/products/count/${specialCd}`,
    params
  );

export const requestSpcLpProductFilter = async <TResponse>(
  specialCd: string,
  type: LpProductFilterType,
  params: LpProductRequest
): Promise<TResponse | undefined> =>
  postSpcLpProduct(
    `/display/api/special/v1/products/filter/${type}/${specialCd}`,
    params
  );

export const requestGetSpcNavigation = async (): Promise<
  SpcNavigationItem[]
> => {
  const { data } = await get<SpcNavigationResponse>({
    url: '/display/api/special/v1/navigation',
    baseUrl: baseURL,
    options,
  });
  return data ?? [];
};

export const requestGetSpcDisplayContents = async ({
  spcType,
  pageNo,
  pageSize,
  recommendItems,
}: SpcDisplayRequestParam): Promise<SpcDisplayContent> => {
  const { data } = await post<SpcDisplayResponse>({
    url: `/display/api/special/v1/special/${spcType}`,
    data: {
      pageNo,
      pageSize,
      recommendItems,
    },
    options,
    baseUrl: baseURL,
  });
  return data;
};

export const requestSpcResultProductInit = async (
  specialCd: string,
  {
    custNo,
    gender,
    sort,
    pageNo = 1,
    pageSize,
    shopCds,
    bcds,
    mcd,
    ccds,
    pcds,
    lcds,
    source,
    device,
    searchType,
    priceSections,
    minPrice,
    maxPrice,
    colors,
    benefits,
    discounts,
    status,
    saleTags,
  }: RequestSpcProductListParams
): Promise<SpcFilterNameResponse['data']> => {
  const { data } = await post<SpcFilterNameResponse>({
    url: `/display/api/special/v1/special/${specialCd}/products/init`,
    options,
    baseUrl: baseURL,
    data: {
      custNo,
      gender,
      sort,
      pageNo,
      pageSize,
      shopCds,
      bcds,
      mcd,
      ccds,
      pcds,
      lcds,
      source,
      device,
      searchType,
      priceSections,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
    },
  });

  return data;
};

export const requestSpcProductResult = async (
  specialCd: string,
  {
    custNo,
    gender,
    sort,
    pageNo,
    pageSize,
    bcds,
    mcd,
    ccds,
    pcds,
    lcds,
    priceSections,
    minPrice,
    maxPrice,
    colors,
    benefits,
    discounts,
    status,
    saleTags,
  }: RequestSpcProductListParams
): Promise<SpcProductResponse['data']> => {
  const { data } = await post<SpcProductResponse>({
    url: `/display/api/special/v1/special/${specialCd}/products`,
    options,
    baseUrl: baseURL,
    data: {
      custNo,
      gender,
      sort,
      pageNo,
      pageSize,
      bcds,
      mcd,
      ccds,
      pcds,
      lcds,
      priceSections,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
    },
  });

  return data;
};

export const requestSpcProductCount = async (
  specialCd: string,
  params: RequestSpcProductListParams
): Promise<SpcProductCountResponse['data']> => {
  const { data } = await post<SpcProductCountResponse>({
    url: `/display/api/special/v1/special/${specialCd}/products/count`,
    options,
    baseUrl: baseURL,
    data: params,
  });

  return data;
};

export const requestSpcProductAggByType = async (
  specialCd: string,
  type: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any
): Promise<SpcAggsResult> => {
  const { data } = await post<SpcAggsResponse>({
    url: `/display/api/special/v1/special/${specialCd}/products/aggs/${type}`,
    baseUrl: baseURL,
    options,
    data: params,
  });

  return data;
};

export const requestSpcTP002 = async ({
  displayAreaId,
  recommendItems,
  specialCd,
}: {
  displayAreaId: number;
  recommendItems: string[];
  specialCd: string;
}) => {
  const { data } = await post<MainDisplayContentResponse>({
    url: `/display/api/special/v1/special/${specialCd}/TP002/${displayAreaId}`,
    data: {
      recommendItems,
    },
    options,
    baseUrl: baseURL,
  });
  return data;
};

// TP003(베스트 아이템)
export const requestSpcTP003 = async ({
  displayAreaId,
  recommendItems,
  specialCd,
}: {
  displayAreaId: number;
  recommendItems: string[];
  specialCd: string;
}) => {
  const { data } = await post<MainDisplayContentResponse>({
    url: `/display/api/special/v1/special/${specialCd}/TP003/${displayAreaId}`,
    data: {
      recommendItems,
    },
    options,
    baseUrl: baseURL,
  });
  return data;
};

export interface SpcTP004ProductListResponse extends CommonResponse {
  data: SpcTP004ProductListContent;
}

export interface SpcTP004ProductListContent {
  productList: Product[];
}

// TP004(브랜드하이라이트 상품 조회)
export const requestSpcTP004ProductList = async ({
  brandCd,
  manualItems,
  recommendItems,
}: {
  brandCd: string;
  manualItems: string[];
  recommendItems: string[];
}) => {
  const { data } = await post<SpcTP004ProductListResponse>({
    url: `/display/api/special/v1/special/TP004/brand-products`,
    data: {
      brandCd,
      manualItems,
      recommendItems,
    },
    options,
    baseUrl: baseURL,
  });
  return data;
};

// 라이징 브랜드 목록 조회
export const requestSpcBrandList = async () => {
  const { data } = await get<SpcBrandListResponse>({
    url: `/display/api/special/v1/brand-round`,
    options,
    baseUrl: baseURL,
  });
  return data ?? [];
};
